import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useTheme } from '../contexts/ThemeContext';
import NewTicketModal from './NewTicketModal';
import NotificationBell from './NotificationBell';
import SearchModal from './SearchModal';
import {
  LayoutDashboard,
  Ticket,
  Plus,
  Settings,
  Users,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Building2,
  Sun,
  Zap,
  Book,
  Moon,
  Workflow,
  LayoutGrid,
  Search,
  Monitor,
  Mail,
  Webhook,
  ClipboardList
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout, isAgent, isAdmin, isSuperAdmin, accessibleOrganizations } = useAuth();
  const { tenant, switchTenant } = useTenant();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Raccourci Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation = [
    ...(isAgent ? [{ name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard }] : []),
    ...(!isAgent ? [{ name: 'Mon espace', href: '/my-dashboard', icon: LayoutDashboard }] : []),
    { name: 'Tickets', href: '/tickets', icon: Ticket },
    ...(isAgent ? [{ name: 'Assets', href: '/assets', icon: Monitor }] : []),
    ...(isAdmin ? [{ name: 'Utilisateurs', href: '/users', icon: Users }] : []),
    ...(isAgent ? [{ name: 'Réponses rapides', href: '/quick-replies', icon: Zap }] : []),
    ...(isAdmin ? [{ name: 'Automatisation', href: '/automation', icon: Workflow }] : []),
    ...(isAdmin ? [{ name: 'Templates email', href: '/email-templates', icon: Mail }] : []),
    ...(isAdmin ? [{ name: 'Webhooks', href: '/webhooks', icon: Webhook }] : []),
    ...(isAdmin ? [{ name: 'Enquêtes', href: '/surveys', icon: ClipboardList }] : []),
    { name: 'Base de connaissances', href: '/kb', icon: Book },
    { name: 'Paramètres', href: '/settings', icon: Settings },
  ];

  const isActive = (href) => location.pathname === href || (href !== '/' && location.pathname.startsWith(href) && href !== '/tickets/new');

  const logoUrl = tenant?.slug ? `/logos/${tenant.slug}.png` : null;
  const hasLogo = logoUrl && !logoError;

  return (
    <div className="h-screen overflow-hidden bg-gray-50 dark:bg-gray-900 flex">
      {/* Sidebar Mobile Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar - fixed, never scrolls */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transform transition-transform lg:translate-x-0 lg:static lg:z-auto flex-shrink-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full overflow-hidden">
          {/* Logo */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            {/* Bouton fermer mobile - positionné en absolu */}
            <button 
              onClick={() => setSidebarOpen(false)} 
              className="lg:hidden absolute top-4 right-4 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>

            {/* Logo ou initiale + titre */}
            {hasLogo ? (
              <div className="flex justify-center">
                <img 
                  src={logoUrl} 
                  alt={tenant?.name || 'Logo'}
                  className="h-10 object-contain"
                  onError={() => setLogoError(true)}
                />
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold" 
                  style={{ backgroundColor: tenant?.primaryColor || '#3B82F6' }}
                >
                  {tenant?.name?.charAt(0) || 'T'}
                </div>
                <div>
                  <h1 className="font-bold text-gray-900 dark:text-white">{tenant?.name || 'Support'}</h1>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Support IT</p>
                </div>
              </div>
            )}

            {/* Org Switcher pour superadmin */}
            {isSuperAdmin && accessibleOrganizations.length > 1 && (
              <div className="mt-3 relative">
                <button
                  onClick={() => setOrgDropdownOpen(!orgDropdownOpen)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  <span className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
                    <Building2 className="w-4 h-4" />
                    {tenant?.name || 'Sélectionner'}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${orgDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {orgDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-10">
                    {accessibleOrganizations.map((org) => (
                      <button
                        key={org.id}
                        onClick={() => {
                          switchTenant(org.slug);
                          setOrgDropdownOpen(false);
                          setLogoError(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 first:rounded-t-lg last:rounded-b-lg flex items-center gap-2 ${tenant?.slug === org.slug ? 'bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-200'}`}
                      >
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: org.primaryColor }} />
                        {org.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* New Ticket Button */}
          <div className="p-4 flex-shrink-0">
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setShowNewTicketModal(true);
                  setSidebarOpen(false);
                }}
                className="flex-1 btn-primary justify-center"
                style={{ backgroundColor: tenant?.primaryColor || '#3B82F6' }}
              >
                <Plus className="w-5 h-5 mr-2" />
                Nouveau ticket
              </button>
              <NotificationBell />
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 pb-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  isActive(item.href)
                    ? 'text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                style={isActive(item.href) ? { backgroundColor: tenant?.primaryColor || '#3B82F6' } : {}}
              >
                <item.icon className="w-5 h-5" />
                {item.name}
              </Link>
            ))}
          </nav>

          {/* User menu */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                {user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium" style={{ backgroundColor: tenant?.primaryColor || '#3B82F6' }}>
                    {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)}
                  </div>
                )}
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{user?.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {userMenuOpen && (
                <div className="absolute bottom-full left-0 right-0 mb-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-10">
                  <button
                    onClick={toggleTheme}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-t-lg"
                  >
                    {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                    {isDark ? 'Mode clair' : 'Mode sombre'}
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-b-lg"
                  >
                    <LogOut className="w-4 h-4" />
                    Déconnexion
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        {/* Top bar mobile */}
        <header className="lg:hidden bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between flex-shrink-0">
          <button onClick={() => setSidebarOpen(true)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <Menu className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
          <div className="flex items-center gap-2">
            {hasLogo ? (
              <img 
                src={logoUrl} 
                alt={tenant?.name || 'Logo'}
                className="h-8 object-contain"
                onError={() => setLogoError(true)}
              />
            ) : (
              <>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold" style={{ backgroundColor: tenant?.primaryColor || '#3B82F6' }}>
                  {tenant?.name?.charAt(0) || 'T'}
                </div>
                <span className="font-semibold text-gray-900 dark:text-white">{tenant?.name}</span>
              </>
            )}
          </div>
          <button 
            onClick={() => setShowNewTicketModal(true)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <Plus className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
        </header>

        {/* Page content - scrollable — key forces full remount on tenant switch */}
        <main className="flex-1 p-4 lg:p-8 overflow-y-auto" key={tenant?.id}>
          {children}
        </main>

        {/* Search Modal */}
        <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      </div>

      {/* New Ticket Modal */}
      <NewTicketModal 
        isOpen={showNewTicketModal} 
        onClose={() => setShowNewTicketModal(false)} 
      />
    </div>
  );
};

export default Layout;